import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ArrowLeft, Trophy, Medal, Crown, Flame, Star } from 'lucide-react';
import { toast } from 'sonner';

const Leaderboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [leaderboardRes, myStatsRes] = await Promise.all([
        axios.get(`${API}/gamification/leaderboard`),
        axios.get(`${API}/gamification/my-stats`)
      ]);
      setLeaderboard(leaderboardRes.data);
      setMyStats(myStatsRes.data);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Crown className="w-6 h-6 text-yellow-400" />;
      case 2:
        return <Medal className="w-6 h-6 text-gray-300" />;
      case 3:
        return <Medal className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="text-lg font-bold text-slate-400">#{rank}</span>;
    }
  };

  const getRankStyle = (rank) => {
    switch (rank) {
      case 1:
        return 'border-yellow-500/50 bg-gradient-to-r from-yellow-500/10 to-amber-500/10';
      case 2:
        return 'border-gray-400/50 bg-gradient-to-r from-gray-400/10 to-slate-400/10';
      case 3:
        return 'border-amber-600/50 bg-gradient-to-r from-amber-600/10 to-orange-600/10';
      default:
        return 'border-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white text-xl">Loading leaderboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-700">
        <div className="container mx-auto px-4 py-4 flex items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-white hover:bg-white/10 mr-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Trophy className="w-6 h-6 text-yellow-400" />
            Leaderboard
          </h1>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {/* My Stats Card */}
        {myStats && (
          <Card className="glass p-6 border border-purple-500/50 mb-8 fade-in">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                <Flame className="w-5 h-5 text-orange-400" />
                Your Stats
              </h3>
              <span className="text-lg font-bold text-purple-400">
                Rank #{myStats.rank} of {myStats.total_users}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400">{myStats.points}</div>
                <div className="text-sm text-slate-400">Points</div>
              </div>
              <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                <div className="text-2xl font-bold text-purple-400">{myStats.badges?.length || 0}</div>
                <div className="text-sm text-slate-400">Badges</div>
              </div>
              <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{myStats.checkin_count}</div>
                <div className="text-sm text-slate-400">Check-ins</div>
              </div>
              <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                <div className="text-2xl font-bold text-blue-400">{myStats.unique_bars_visited}</div>
                <div className="text-sm text-slate-400">Bars Visited</div>
              </div>
            </div>
            {/* Badges Display */}
            {myStats.badges && myStats.badges.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-700">
                <p className="text-sm text-slate-400 mb-2">Your Badges:</p>
                <div className="flex flex-wrap gap-2">
                  {myStats.badges.map((badge, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-1 px-3 py-1 bg-purple-500/20 rounded-full text-sm"
                      title={badge.description}
                    >
                      <span>{badge.icon}</span>
                      <span className="text-purple-300">{badge.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {/* Leaderboard List */}
        <Card className="glass p-6 border border-slate-700 fade-in">
          <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-400" />
            Top Bar Buzzers
          </h3>
          <div className="space-y-3">
            {leaderboard.map((entry) => (
              <div
                key={entry.user_id}
                className={`flex items-center gap-4 p-4 rounded-lg border ${getRankStyle(entry.rank)} ${entry.user_id === user?.id ? 'ring-2 ring-purple-500' : ''}`}
              >
                <div className="w-10 flex justify-center">
                  {getRankIcon(entry.rank)}
                </div>
                <Avatar className="w-12 h-12 border-2 border-slate-600">
                  {entry.profile_picture_url ? (
                    <AvatarImage src={`${process.env.REACT_APP_BACKEND_URL}${entry.profile_picture_url}`} />
                  ) : null}
                  <AvatarFallback className="bg-purple-600 text-white">
                    {entry.username?.[0]?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className={`font-semibold ${entry.user_id === user?.id ? 'text-purple-400' : 'text-white'}`}>
                    {entry.username}
                    {entry.user_id === user?.id && <span className="text-xs ml-2">(You)</span>}
                  </p>
                  <p className="text-sm text-slate-400">Bar Buzzer</p>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-yellow-400">{entry.points}</div>
                  <div className="text-xs text-slate-400">points</div>
                </div>
              </div>
            ))}
          </div>
          {leaderboard.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              No users on the leaderboard yet. Be the first to check in!
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Leaderboard;
